import { User } from './../../types/user';
import { createSlice, createSelector, createEntityAdapter } from '@reduxjs/toolkit';
import {
    deleteLicence,
    getLicences,
    getUser,
    relogIn,
    signIn,
    updateProfile,
    updateUserSetting,
} from '../actions/userActions';
import { RootState } from '../store';
import { getAllPois, getPois } from '../actions/poiActions';
import { getAllGeofences, getDogGeofences, getGeofences } from '../actions/geofenceActions';
import { colors } from '../../utils/colors';
import { TagTypeKey } from '../../types/tags';

export const GEO_MODES = {
    geofence: 'geofence',
    huntingGround: 'huntingGround',
};
export const SUB_AREAS = {
    forbidden_area: 'forbidden_area',
    sub_area: 'sub_area',
};

export type License = {
    id: number;
    user_id: number;
    name: string;
    file: string;
};
export const usersAdapter = createEntityAdapter();
type TagImage = {
    id: number;
    poi_id: number;
    file: string;
};

export type Tag = {
    id: number;
    images: TagImage[];
    latitude: number;
    longitude: number;
    name: string;
    type: TagTypeKey;
    sub_type: string;
    animal_type: string;
    user_id: number;
    description: string;
    team_id?: number;
    team_user_id?: number;
};

export type LatLng = {
    lat: number;
    lng: number;
};
export type LatitudeLongitude = {
    latitude: number;
    longitude: number;
};
export type LatLat = LatLng & LatitudeLongitude;

export const InitialGeofence = {
    id: -1,
    coordinates: [] as LatLng[],
    name: '',
    color: '',
    itemBackgroundColor: '',
    attributes: { color: '', exitAlarm: true, enterAlarm: false, dogs: '', users: '' },
    description: '',
    subAreas: [],
};

export type Geofence = {
    id: number;
    coordinates: LatLng[];
    area: string;
    attributes: {
        color: string;
    };
    type: string;
    subType?: string;
    subAreas: Geofence[];
    description: string;
    name: string;
    color: string | null;
    itemBackgroundColor: string;
    team_id?: number;
    team_user_id?: number;
};

const INITIAL_STATE = {
    isLoggedIn: false,
    data: {} as User,
    userId: -1,
    language: 'en',
    credentials: {
        email: '',
        password: '',
    },
    rememberMe: 0,
    pois: [] as Tag[],
    editingGeoFenceStatus: false,
    editingGeoFence: InitialGeofence,
    geofences: [] as Geofence[],
    dogsGeofences: [] as Geofence[],
    geofenceMode: 'huntingGround',
    subAreaType: false as boolean | string,
    editingPoi: {
        coordinates: [],
        name: '',
        description: '',
        type: '',
    },
    licences: [] as License[],
    reLoggedIn: -1,
    showNewLicenceModal: false,
    licenceUploadProgress: 0,
};

const userReducer = createSlice({
    name: 'user',
    initialState: usersAdapter.getInitialState(INITIAL_STATE),
    reducers: {
        setReLoggedIn(state, action) {
            state.reLoggedIn = action.payload;
        },
        setUploadLicenceProgress(state, action) {
            state.licenceUploadProgress = action.payload;
        },
        setIsLogged(state, action) {
            state.isLoggedIn = action.payload;
        },
        setRememberMe(state, action) {
            state.rememberMe = action.payload;
        },
        setLanguage(state, action) {
            state.language = action.payload;
        },
        setCredentials(state, action) {
            state.credentials = action.payload;
        },
        deleteGeofence(state, action) {
            state.geofences = state.geofences.filter((g) => g.id !== action.payload.id);
        },
        setGeofences(state, action) {
            state.geofences = action.payload ?? [];
        },
        deleteDogGeofence(state, action) {
            state.dogsGeofences = state.dogsGeofences.filter((g) => g.id !== action.payload.id);
        },
        setDogsGeofence(state, action) {
            state.dogsGeofences = action.payload ?? [];
        },
        setEditingGeoFenceStatus(state, action) {
            state.editingGeoFenceStatus = action.payload;
        },
        setSubAreaType(state, action) {
            state.subAreaType = action.payload;
        },
        setEditingGeoFence(state, action) {
            state.editingGeoFence = action.payload;
        },
        setGeofenceMode(state, action) {
            state.geofenceMode = action.payload;
        },
        setPois(state, action) {
            state.pois = action.payload;
        },
        resetPois(state) {
            state.pois = [];
        },
        resetGeofences(state) {
            state.geofences = [];
        },
        setEditingPoi(state, action) {
            state.editingPoi = action.payload;
        },
        setShowNewLicenceModal(state, action) {
            state.showNewLicenceModal = action.payload;
        },
        updateUserSettings(state, action) {
            const updatedData = { ...state.data, [action.payload.setting]: action.payload.value };
            // return { ...state, data: updatedData };
            state.data = updatedData;
        },

        logOut(state) {
            state.isLoggedIn = false;
            // state.credentials = {
            //     email: '',
            //     password: '',
            // };
            state.data = {} as User;
            state.userId = -1;
        },
        sayGoodBye() {
            return usersAdapter.getInitialState(INITIAL_STATE);
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(signIn.fulfilled, (state, action) => {
                // console.log('signIn', action.payload);
                state.data = action.payload;
                state.userId = action.payload.id;
                state.language = action.payload.language ?? 'en';
                state.isLoggedIn = true;
                state.reLoggedIn = -1;
            })
            .addCase(signIn.rejected, () => {})
            .addCase(relogIn.fulfilled, (state, action) => {
                // console.log('signIn', action.payload);
                if (action.payload.status === 200 && typeof action.payload.data === 'object') {
                    state.reLoggedIn = -1;
                    state.data = action.payload.data;
                    state.userId = action.payload.data.id;
                    state.isLoggedIn = true;
                } else {
                    state.reLoggedIn = state.reLoggedIn + 1;
                }
            })
            .addCase(relogIn.rejected, () => {})
            .addCase(getUser.fulfilled, (state, action) => {
                state.data = action.payload;
            })
            .addCase(getUser.rejected, () => {
                // console.log('getUser r', action);
            })
            .addCase(updateProfile.fulfilled, (state, action) => {
                state.data = action.payload;
            })
            .addCase(updateProfile.rejected, () => {
                // console.log('updateProfile r', action.error);
            })
            .addCase(getPois.fulfilled, () => {
                // state.pois = action.payload;
            })
            .addCase(getPois.rejected, () => {
                // console.log('updateProfile r', action.error);
            })
            .addCase(getAllPois.fulfilled, (state, action) => {
                state.pois = action.payload;
            })
            .addCase(getAllPois.rejected, () => {
                // console.log('getAllPois r', action.error);
            })
            .addCase(getGeofences.fulfilled, () => {
                // state.geofences = action.payload;
            })
            .addCase(getGeofences.rejected, () => {
                // console.log('updateProfile r', action.error);
            })
            .addCase(getAllGeofences.fulfilled, (state, action) => {
                state.geofences = action.payload;
            })
            .addCase(getAllGeofences.rejected, () => {
                // console.log('updateProfile r', action.error);
            })
            .addCase(getDogGeofences.fulfilled, (state, action) => {
                state.dogsGeofences = action.payload;
            })
            .addCase(getDogGeofences.rejected, () => {
                // console.log('updateProfile r', action.error);
            })
            .addCase(getLicences.fulfilled, (state, action) => {
                state.licences = action.payload;
            })
            .addCase(getLicences.rejected, () => {
                // console.log('updateProfile r', action.error);
            })
            .addCase(updateUserSetting.fulfilled, (state, action) => {
                state.data = { ...state.data, ...action.payload };
            })
            .addCase(updateUserSetting.rejected, () => {
                // console.log('updateProfile r', action.error);
            })
            .addCase(deleteLicence.rejected, () => {
                // console.log('deleteLicence r', action.error);
            });
    },
});

export const {
    setReLoggedIn,
    setIsLogged,
    setRememberMe,
    setCredentials,
    setLanguage,
    logOut,
    sayGoodBye,
    setGeofenceMode,
    setEditingGeoFenceStatus,
    setEditingGeoFence,
    setSubAreaType,
    deleteDogGeofence,
    deleteGeofence,
    resetPois,
    resetGeofences,
    updateUserSettings,
    setShowNewLicenceModal,
    setUploadLicenceProgress,
} = userReducer.actions;

export default userReducer.reducer;

const userState$ = (s: RootState) => s.user;
const mapState$ = (s: RootState) => s.map;
const teamState$ = (s: RootState) => s.team;

const activeTeamId$ = createSelector(teamState$, (s) => s.activeTeam);
export const userId$ = createSelector(userState$, (s) => s.userId);
export const isLoggedIn$ = createSelector(userState$, (s) => s.isLoggedIn);
export const rememberMe$ = createSelector(userState$, (s) => s.rememberMe);
export const credentials$ = createSelector(userState$, (s) => s.credentials);
export const language$ = createSelector(userState$, (s) => s.language);

export const rawTags$ = createSelector(userState$, (s) => s.pois);
export const rawDisabledTagFilters$ = createSelector(mapState$, (s) => s.disabledTagFilters ?? []);

export const byTeamTags$ = createSelector(
    rawTags$,
    activeTeamId$,
    (tags, activeTeamId) =>
        tags?.filter((g) => {
            if (!activeTeamId) {
                return !g.team_id;
            } else {
                return g.team_id === activeTeamId;
            }
        }),
);

export const filteredTags$ = createSelector(byTeamTags$, rawDisabledTagFilters$, (tags, filters) =>
    tags.filter((p) => !filters.includes(p.type)),
);

export const rawGeofences$ = createSelector(userState$, (s) => s.geofences);
export const rawDogGeofences$ = createSelector(userState$, (s) => s.dogsGeofences);
export const editingGeoFence$ = createSelector(userState$, (s) => s.editingGeoFence);

export const preparedGeofences$ = createSelector(rawGeofences$, (geofences) =>
    geofences.reduce<
        {
            area: string;
            attributes: { color: string };
            color: string;
            coordinates: LatLng[];
            description: string;
            id: number;
            itemBackgroundColor: string;
            name: string;
            subAreas: Geofence[];
            team_id?: number;
            team_user_id?: number;
            type: string;
        }[]
    >((data, geofence) => {
        const coordinates = geofence.area
            .replace(/POLYGON\s?\(\((.+)\)\)/g, '$1')
            .split(', ')
            .map((el) => {
                const coordinates = el.split(' ');
                return { lat: parseFloat(coordinates[0]), lng: parseFloat(coordinates[1]) };
            });
        if (coordinates && coordinates.length > 0) {
            const firstPoint = coordinates[0];
            coordinates.push(firstPoint);
        }

        let itemBackgroundColor = 'yellow';

        if (geofence.attributes && geofence.attributes?.color) {
            itemBackgroundColor = geofence.attributes.color;
        }

        const subAreas =
            geofence.subAreas.map((sub) => {
                const coordinates = sub.area
                    ?.replace(/POLYGON\s?\(\((.+)\)\)/g, '$1')
                    .split(', ')
                    .map((el: any) => {
                        const coordinates = el.split(' ');
                        return { lat: parseFloat(coordinates[0]), lng: parseFloat(coordinates[1]) };
                    });

                if (coordinates && coordinates.length > 0) {
                    const firstPoint = coordinates[0];
                    coordinates.push(firstPoint);
                }

                let itemBackgroundColor = colors.red;

                if (sub.attributes && sub.attributes?.color) {
                    itemBackgroundColor = sub.attributes.color;
                }

                return {
                    area: sub.area,
                    attributes: sub.attributes,
                    color: itemBackgroundColor,
                    coordinates,
                    description: sub.description,
                    id: sub.id,
                    itemBackgroundColor,
                    name: sub.name,
                    subAreas: [],
                    type: sub.description,
                };
            }) ?? [];

        data.push({
            area: geofence.area,
            attributes: geofence.attributes,
            coordinates,
            color: itemBackgroundColor,
            description: geofence.description,
            id: geofence.id,
            itemBackgroundColor,
            name: geofence.name,
            subAreas,
            team_id: geofence.team_id,
            team_user_id: geofence.team_user_id,
            type: geofence.description,
        });
        return data;
    }, []),
);

export const preparedDogGeofences$ = createSelector(rawDogGeofences$, (geofences) =>
    geofences.reduce<
        {
            coordinates: LatLng[];
            id: number;
            area: string;
            color: string;
            description: string;
            attributes: { color: string };
            itemBackgroundColor: string;
            subAreas: Geofence[];
            name: string;
            type: string;
            team_id?: number;
            team_user_id?: number;
        }[]
    >((data, geofence) => {
        const coordinates = geofence.area
            .replace(/POLYGON\s?\(\((.+)\)\)/g, '$1')
            .split(', ')
            .map((el) => {
                const coordinates = el.split(' ');
                return { lat: parseFloat(coordinates[0]), lng: parseFloat(coordinates[1]) };
            });
        if (coordinates && coordinates.length > 0) {
            const firstPoint = coordinates[0];
            coordinates.push(firstPoint);
        }

        let itemBackgroundColor = 'yellow';

        if (geofence.attributes && geofence.attributes?.color) {
            itemBackgroundColor = geofence.attributes.color;
        }

        data.push({
            description: geofence.description,
            area: '',
            coordinates,
            attributes: geofence.attributes,
            team_id: geofence.team_id,
            team_user_id: geofence.team_user_id,
            id: geofence.id,
            itemBackgroundColor,
            color: itemBackgroundColor,
            subAreas: [],
            name: geofence.name,
            type: geofence.description,
        });
        return data;
    }, []),
);

export const filteredGeofences$ = createSelector(
    preparedGeofences$,
    editingGeoFence$,
    activeTeamId$,
    (geofences, editing, activeTeamId) =>
        geofences
            ?.filter((g) => g.id !== editing?.id)
            ?.filter((g) => {
                if (!activeTeamId) {
                    return !g.team_id;
                } else {
                    return g.team_id === activeTeamId;
                }
            }),
);

export const filteredTeamsGeofences$ = createSelector(
    preparedGeofences$,
    (geofences) =>
        geofences?.filter((g) => {
            return g.team_id;
        }),
);

export const activeTeamGeofence$ = createSelector(
    preparedGeofences$,
    activeTeamId$,
    (geofences, activeTeamId) => geofences?.find((g) => g.team_id === activeTeamId),
);

export const userHuntingGrounds$ = createSelector(
    preparedGeofences$,
    (geofences) =>
        geofences?.filter((g) => {
            return !g.team_id;
        }),
);

export const filteredDogGeofences$ = createSelector(
    preparedDogGeofences$,
    editingGeoFence$,
    (geofences, editing) => geofences?.filter((g) => g.id !== editing?.id),
);

export const hasCurrentGeofence$ = createSelector(
    editingGeoFence$,
    (editingGeoFence) =>
        !(editingGeoFence === undefined || !editingGeoFence?.coordinates || editingGeoFence.coordinates.length === 0),
);
